import { useState } from 'react';
import {
  Chrome,
  Loader2,
  Trash2,
  ListTodo,
  Download,
  BookOpen,
  HelpCircle,
  MonitorSmartphone,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useUiStore } from '../../store/ui';
import { useExtTokens, useRevokeExtToken, useExtUpdateAvailable } from '../../hooks/useExtTokens';
import { useExtTasks, useDeleteExtTask, type ExtTask } from '../../hooks/useExtTasks';
import { EXT_VERSION, EXT_ZIP_URL } from '../../lib/extension';

// URL pubblico dello zip dell'estensione (stesso file servito da apps/web/public/downloads/).
// Duplicato qui e in LinkedInView per evitare un import circolare

/**
 * TAB Estensione (Impostazioni): tutto il necessario per collegare l'estensione
 * Chrome del CRM, in ordine di utilizzo:
 *   1. download dello zip + link alla guida completa (pagina LinkedIn);
 *   2. dispositivi collegati (l'accesso avviene DALL'ESTENSIONE con
 *      email+password della webmail: qui si vedono e si scollegano);
 *   3. coda attività copilota;
 *   4. problemi frequenti.
 * Il token-dispositivo generato dal login dell'estensione è interno e non
 * viene mai mostrato all'utente.
 */
export function ExtensionTab() {
  const { data: tokens, isLoading } = useExtTokens();
  const revokeToken = useRevokeExtToken();
  const openLinkedIn = useUiStore((s) => s.openLinkedIn);
  const closeSettings = useUiStore((s) => s.closeSettings);
  const setManualiTarget = useUiStore((s) => s.setManualiTarget);
  const setManualiScope = useUiStore((s) => s.setManualiScope);

  const [busyId, setBusyId] = useState<string | null>(null);

  // "Scollega" = revoca il token-dispositivo: l'estensione su quel browser
  // smette di funzionare finché non rifà l'accesso.
  const disconnect = async (id: string) => {
    if (!window.confirm('Scollegare questo dispositivo? L’estensione su quel browser smetterà di funzionare finché non rifarà l’accesso.')) return;
    setBusyId(id);
    try {
      await revokeToken.mutateAsync(id);
    } finally {
      setBusyId(null);
    }
  };

  // Apre la guida completa: pagina LinkedIn → tab Estensione, chiudendo le
  // Impostazioni. Precarica anche il target del manuale ('linkedin'): se da lì
  // l'utente apre poi "Apri il manuale", arriva già scrollato alle FAQ
  // dell'Assistente LinkedIn.
  const openGuide = () => {
    setManualiTarget('linkedin');
    setManualiScope('linkedin');
    openLinkedIn('extension');
    closeSettings();
  };

  return (
    <div className="space-y-6">
      {/* Banner: nuova versione estensione disponibile (se un dispositivo è obsoleto) */}
      <ExtUpdateBanner />
      {/* 1) Intestazione + azioni principali: download e guida completa */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <Chrome size={14} className="text-accent-400" /> Estensione Chrome
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-zinc-500">
          L&rsquo;estensione salva post, profili ed engagement da LinkedIn direttamente nel CRM
          della webmail. Per usarla: scarica lo zip, installala in Chrome e accedi
          dall&rsquo;estensione con le stesse email e password della webmail.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={EXT_ZIP_URL}
            download
            className="wm-btn-accent flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition"
          >
            <Download size={13} /> Scarica l&rsquo;estensione (v{EXT_VERSION})
          </a>
          <button
            onClick={openGuide}
            className="wm-btn-ghost flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition"
          >
            <BookOpen size={13} /> Apri guida completa
          </button>
        </div>
      </div>

      {/* 2) Dispositivi collegati: un elemento per ogni browser/PC che ha fatto
             l'accesso dall'estensione (l'etichetta è nome dispositivo + data) */}
      <div className="space-y-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
          <MonitorSmartphone size={12} className="text-accent-400" /> Dispositivi collegati
        </h4>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-xs text-zinc-500">
            <Loader2 size={13} className="animate-spin" /> Caricamento…
          </div>
        ) : !tokens || tokens.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-800 py-6 text-center text-xs text-zinc-500">
            Nessun dispositivo collegato. Installa l&rsquo;estensione e accedi con le credenziali
            della webmail.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800 overflow-hidden rounded-md border border-zinc-800">
            {tokens.map((t) => {
              const revoked = !!t.revoked_at;
              return (
                <li key={t.id} className={'flex items-center justify-between gap-3 px-3 py-2 ' + (revoked ? 'opacity-50' : '')}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs font-medium text-zinc-200">{t.label}</span>
                      {revoked && (
                        <span className="rounded bg-rose-950/50 px-1.5 py-0.5 text-[10px] font-medium text-rose-300">
                          scollegato
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      collegato il {format(new Date(t.created_at), 'd MMM yyyy', { locale: it })}
                      {' · '}
                      {t.last_used_at
                        ? `ultimo uso ${format(new Date(t.last_used_at), 'd MMM yyyy HH:mm', { locale: it })}`
                        : 'mai usato'}
                    </div>
                  </div>
                  {!revoked && (
                    <button
                      onClick={() => disconnect(t.id)}
                      disabled={busyId === t.id}
                      className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:bg-rose-950/40 hover:text-rose-300 disabled:opacity-50"
                      title="Scollega questo dispositivo"
                    >
                      {busyId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Scollega
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 3) Coda attività copilota */}
      <ExtTasksSection />

      {/* 4) Problemi frequenti */}
      <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/30 p-4">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
          <HelpCircle size={12} className="text-accent-400" /> Problemi frequenti
        </h4>
        <ul className="list-disc space-y-1.5 pl-4 text-xs leading-relaxed text-zinc-400">
          <li>
            Se dopo il download compare la finestra <b className="text-zinc-200">&laquo;Salva con nome&raquo;</b> di
            Windows, completala o annullala: finché è aperta la pagina non riceve i click.
          </li>
          <li>
            L&rsquo;estensione non compare su LinkedIn? Verifica in{' '}
            <code className="rounded bg-zinc-800/80 px-1 py-0.5 font-mono text-[10px] text-zinc-300">chrome://extensions</code>{' '}
            che sia attiva, poi ricarica la pagina di LinkedIn.
          </li>
          <li>
            L&rsquo;estensione risulta scollegata? Il dispositivo potrebbe essere stato scollegato
            da questo elenco: apri il popup dell&rsquo;estensione e rifai l&rsquo;accesso con email
            e password della webmail.
          </li>
          <li>
            Cambi browser o PC? Basta <b className="text-zinc-200">rifare l&rsquo;accesso
            dall&rsquo;estensione</b>: il nuovo dispositivo compare in questo elenco e puoi
            scollegare quello vecchio.
          </li>
        </ul>
      </div>
    </div>
  );
}

// Etichette leggibili per i tipi di task della coda copilota
const TASK_TYPE_LABELS: Record<string, string> = {
  visit_profile: 'Visita profilo',
  comment_post: 'Commenta post',
  send_dm: 'Invia messaggio',
  custom: 'Personalizzata',
};

// Badge di stato del task: in attesa / fatto / saltato
function TaskStatusBadge({ status }: { status: string }) {
  const cls =
    status === 'pending'
      ? 'bg-amber-950/50 text-amber-300'
      : status === 'done'
        ? 'bg-emerald-950/50 text-emerald-300'
        : 'bg-zinc-800/80 text-zinc-400';
  const label = status === 'pending' ? 'in attesa' : status === 'done' ? 'fatto' : 'saltato';
  return <span className={'rounded px-1.5 py-0.5 text-[10px] font-medium ' + cls}>{label}</span>;
}

/**
 * Sezione "Coda attività": elenco dei task copilota accodati per l'estensione
 * (da ContactsView o altre viste). I task in attesa si possono eliminare;
 * quelli chiusi restano visibili come storico recente.
 */
export function ExtTasksSection() {
  // isFetching + refetch: alimentano il bottone "Aggiorna" (niente più
  // esci/rientra per vedere i profili appena visitati).
  const { data: tasks, isLoading, isFetching, refetch } = useExtTasks();
  const deleteTask = useDeleteExtTask();
  const [busyId, setBusyId] = useState<string | null>(null);

  const remove = async (id: string) => {
    setBusyId(id);
    try {
      await deleteTask.mutateAsync(id);
    } finally {
      setBusyId(null);
    }
  };

  // Contenitore .wm-list: applica il tema chiaro degli elenchi Posta (tabella
  // wm-table, righe wm-row, badge) a prescindere dal parent, così TUTTI gli
  // elenchi hanno la stessa grafica webmail.
  return (
    <section
      className="wm-list flex flex-col overflow-hidden rounded-xl border"
      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
    >
      <div className="shrink-0 border-b px-4 pb-3 pt-3" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <ListTodo size={15} style={{ color: 'var(--accent)' }} />
          <h4 className="text-[14px] font-semibold" style={{ color: 'var(--accent-dark)' }}>
            Coda attività
          </h4>
          {tasks && (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              · {tasks.length}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="wm-chip ml-auto flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium disabled:opacity-60"
            title="Ricarica la coda per vedere le attività aggiornate"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Aggiorna
          </button>
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed" style={{ color: 'var(--muted)' }}>
          Le attività accodate qui compaiono nel popup dell&rsquo;estensione: con &laquo;Apri&raquo; si
          apre la pagina e <b>sei tu</b> a eseguire l&rsquo;azione su LinkedIn, poi la marchi come
          fatta o saltata. Nessuna automazione: è solo una lista di promemoria (anti-ban).
        </p>
      </div>

      <div className="overflow-y-auto px-4 pb-4 pt-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={16} className="animate-spin" style={{ color: 'var(--muted)' }} />
          </div>
        ) : !tasks || tasks.length === 0 ? (
          <div
            className="rounded-md border border-dashed py-8 text-center text-xs"
            style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
          >
            Nessuna attività in coda. Accodane una dalla scheda di un contatto
            (&laquo;Pianifica visita&raquo;).
          </div>
        ) : (
          <table className="wm-table w-full border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Data</th>
                <th style={{ width: 150 }}>Attività</th>
                <th>Contatto / Note</th>
                <th style={{ width: 100 }}>Stato</th>
                <th style={{ width: 130 }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t: ExtTask) => (
                <tr key={t.id} className="wm-row">
                  <td className="whitespace-nowrap" style={{ color: 'var(--muted)' }}>
                    {format(new Date(t.created_at), 'd MMM', { locale: it })}
                  </td>
                  <td className="font-medium">{TASK_TYPE_LABELS[t.task_type] ?? t.task_type}</td>
                  <td className="max-w-0 truncate" title={t.note ?? t.target_url}>
                    {t.contacts?.full_name ?? t.target_url}
                    {t.note ? ` · ${t.note}` : ''}
                  </td>
                  <td>
                    <TaskStatusBadge status={t.status} />
                  </td>
                  <td>
                    {t.status === 'pending' && (
                      <span className="flex items-center gap-1">
                        <a
                          href={t.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium transition hover:bg-[var(--surface-low)]"
                          style={{ color: 'var(--accent)' }}
                          title="Apri la pagina su LinkedIn (l'azione la esegui tu)"
                        >
                          <ExternalLink size={12} /> Apri
                        </a>
                        <button
                          onClick={() => remove(t.id)}
                          disabled={busyId === t.id}
                          className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                          style={{ color: 'var(--muted)' }}
                          title="Rimuovi questa attività dalla coda"
                        >
                          {busyId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}


// ---------- Banner "nuova versione estensione disponibile" ----------
// Compare nella sezione Estensione se almeno un dispositivo collegato usa una
// versione più vecchia di quella pubblicata (EXT_VERSION). I dispositivi
// riportano la loro versione all'API tramite l'header X-Ext-Version.
export function ExtUpdateBanner() {
  const updateAvailable = useExtUpdateAvailable();
  if (!updateAvailable) return null;
  return (
    <div className="flex items-start gap-3 rounded-lg border-2 border-violet-300 bg-violet-50 p-4 text-violet-950 shadow-sm">
      <AlertTriangle size={20} className="mt-0.5 shrink-0 text-violet-500" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-violet-950">
          Nuova versione dell&rsquo;estensione disponibile (v{EXT_VERSION})
        </div>
        <p className="mt-0.5 text-xs font-medium leading-relaxed text-violet-800">
          Uno o piu&rsquo; dei tuoi dispositivi usa una versione piu&rsquo; vecchia. Scarica lo zip
          aggiornato e ricaricalo in Chrome (chrome://extensions &rarr; Ricarica) per avere le
          ultime funzioni e correzioni.
        </p>
        <a
          href={EXT_ZIP_URL}
          download
          className="wm-btn-accent mt-2.5 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition"
        >
          <Download size={13} /> Scarica v{EXT_VERSION}
        </a>
      </div>
    </div>
  );
}
